import {
  OPEN_OPPORTUNITY_STAGES,
  OPPORTUNITY_STAGE_LABELS,
  type OpportunityDTO,
  type OpportunityListResponse,
  type OpportunityStage,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KanbanSquare, List, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { MoneyText, OwnerAvatar, TierBadge } from '../../components/crm';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DropdownMenu,
  EmptyState,
  Field,
  Input,
  Select,
  SidePanel,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from '../../components/ui';
import { useLookups } from '../../hooks/useMeta';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';

type Mode = 'board' | 'list';
const BOARD_STAGES: OpportunityStage[] = [...OPEN_OPPORTUNITY_STAGES];

/**
 * The pipeline: every open deal by stage, with the totals a salesperson reports on. Moving a card applies the
 * same lead-level effects as the profile (quote sent → quote follow-up clock, won → customer).
 */
export function OpportunitiesPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const mode: Mode = params.get('view') === 'list' ? 'list' : 'board';
  const owner = params.get('owner') ?? '';
  const closing = params.get('closing') ?? '';
  const q = params.get('q') ?? '';
  const [lostFor, setLostFor] = useState<OpportunityDTO | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const query = {
    owner: owner || undefined,
    closing: closing || undefined,
    q: q || undefined,
    stage: mode === 'board' ? BOARD_STAGES.join(',') : undefined,
    sort: 'value',
    dir: 'desc',
    pageSize: 200,
  };
  const { data, isLoading } = useQuery({
    queryKey: ['opportunities', 'list', query],
    queryFn: ({ signal }) => api.get<OpportunityListResponse>(apiUrl('/api/opportunities', query), { signal }),
  });

  const move = useMutation({
    mutationFn: (input: { id: string; stage: OpportunityStage; lostReason?: string }) =>
      api.post(`/api/opportunities/${input.id}/stage`, { stage: input.stage, lostReason: input.lostReason }),
    onSuccess: async (_result, input) => {
      toast.success(`Moved to ${OPPORTUNITY_STAGE_LABELS[input.stage]}`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['opportunities'] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const moveTo = (opportunity: OpportunityDTO, stage: OpportunityStage) => {
    if (stage === 'lost') setLostFor(opportunity);
    else move.mutate({ id: opportunity.id, stage });
  };

  const items = data?.items ?? [];
  const totals = data?.totals;

  return (
    <Page width="full">
      <PageHeader
        title="Opportunities"
        description="Every open deal, where it stands and what it is worth."
        actions={
          <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5">
            <Button size="sm" icon={KanbanSquare} variant={mode === 'board' ? 'primary' : 'ghost'} onClick={() => setParam('view', '')}>
              Board
            </Button>
            <Button size="sm" icon={List} variant={mode === 'list' ? 'primary' : 'ghost'} onClick={() => setParam('view', 'list')}>
              List
            </Button>
          </div>
        }
      />

      {totals && (
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <Stat label="Open deals" value={String(totals.openCount)} />
          <Stat label="Open pipeline" value={formatMoney(totals.openValue, 'CAD', { compact: true })} />
          <Stat label="Weighted" value={formatMoney(totals.weightedValue, 'CAD', { compact: true })} />
          <Stat label="Won (90 days)" value={formatMoney(totals.wonValue90d, 'CAD', { compact: true })} />
        </div>
      )}

      <Card className="mb-4">
        <CardBody className="flex flex-wrap items-center gap-2">
          <Input className="max-w-xs" placeholder="Search deals or pizzerias" defaultValue={q} onBlur={(event) => setParam('q', event.target.value)} />
          <OwnerFilter value={owner} onChange={(value) => setParam('owner', value)} />
          <Select value={closing} onChange={(event) => setParam('closing', event.target.value)} placeholder="Any close date">
            <option value="this_month">Closing this month</option>
            <option value="next_90_days">Closing in 90 days</option>
            <option value="overdue">Past expected close</option>
          </Select>
        </CardBody>
      </Card>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={TrendingUp}
            title="No deals match"
            description="Deals are created from a lead's profile (the Deal button) once a conversation turns into a real opportunity."
            action={
              <Link to="/leads">
                <Button>Go to leads</Button>
              </Link>
            }
          />
        </Card>
      ) : mode === 'board' ? (
        <div className="grid gap-3 overflow-x-auto lg:grid-cols-4">
          {BOARD_STAGES.map((stage) => {
            const column = items.filter((item) => item.stage === stage);
            const stageTotals = totals?.byStage[stage];
            return (
              <div key={stage} className="min-w-[240px] rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
                  <span className="text-[13px] font-semibold text-slate-800">{OPPORTUNITY_STAGE_LABELS[stage]}</span>
                  <span className="text-xs text-slate-500">
                    {stageTotals?.count ?? 0} · {formatMoney(stageTotals?.value ?? 0, 'CAD', { compact: true })}
                  </span>
                </div>
                <div className="space-y-2 p-2">
                  {column.map((opportunity) => (
                    <DealCard key={opportunity.id} opportunity={opportunity} onOpen={() => setOpenId(opportunity.id)} onMove={(to) => moveTo(opportunity, to)} />
                  ))}
                  {column.length === 0 && <p className="px-1 py-3 text-center text-xs text-slate-400">No deals</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <Table>
            <THead>
              <TR>
                <TH>Deal</TH>
                <TH>Pizzeria</TH>
                <TH>Stage</TH>
                <TH align="right">Value</TH>
                <TH align="right">Weighted</TH>
                <TH>Expected close</TH>
                <TH>Days in stage</TH>
                <TH>Owner</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((opportunity) => (
                <TR key={opportunity.id} interactive onClick={() => setOpenId(opportunity.id)}>
                  <TD className="font-medium text-slate-900">{opportunity.title}</TD>
                  <TD>
                    <Link to={`/leads/${opportunity.businessId}?tab=deals`} className="hover:text-ember-700" onClick={(event) => event.stopPropagation()}>
                      {opportunity.businessName}
                    </Link>
                  </TD>
                  <TD>
                    <StageBadge stage={opportunity.stage} />
                  </TD>
                  <TD numeric>
                    <MoneyText amount={opportunity.estimatedValue} currency={opportunity.currency} />
                  </TD>
                  <TD numeric>
                    <MoneyText amount={opportunity.weightedValue} currency={opportunity.currency} />
                  </TD>
                  <TD>{opportunity.expectedCloseDate ? formatDate(opportunity.expectedCloseDate) : '—'}</TD>
                  <TD>{opportunity.daysInStage}</TD>
                  <TD>
                    <OwnerAvatar userId={opportunity.ownerId} showName />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {lostFor && (
        <LostReasonPanel
          opportunity={lostFor}
          onClose={() => setLostFor(null)}
          onConfirm={(reason) => {
            move.mutate({ id: lostFor.id, stage: 'lost', lostReason: reason });
            setLostFor(null);
          }}
        />
      )}
      {openId && <DealDrawer id={openId} onClose={() => setOpenId(null)} onMove={moveTo} />}
    </Page>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
        <p className="text-lg font-semibold tabular-nums text-slate-900">{value}</p>
      </CardBody>
    </Card>
  );
}

function StageBadge({ stage }: { stage: OpportunityStage }) {
  const color = stage === 'won' ? 'green' : stage === 'lost' ? 'red' : stage === 'negotiation' ? 'rose' : stage === 'qualifying' ? 'sky' : 'amber';
  return <Badge color={color}>{OPPORTUNITY_STAGE_LABELS[stage]}</Badge>;
}

function OwnerFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { meta } = useLookups();
  return (
    <Select value={value} onChange={(event) => onChange(event.target.value)} placeholder="Any owner">
      <option value="me">Mine</option>
      {(meta?.users ?? []).map((user) => (
        <option key={user.id} value={user.id}>
          {user.name}
        </option>
      ))}
    </Select>
  );
}

function DealCard({ opportunity, onOpen, onMove }: { opportunity: OpportunityDTO; onOpen: () => void; onMove: (stage: OpportunityStage) => void }) {
  const overdue = opportunity.expectedCloseDate && new Date(opportunity.expectedCloseDate) < new Date();
  return (
    <div className="rounded-md border border-slate-200 bg-surface p-2.5 shadow-xs">
      <div className="flex items-start justify-between gap-2">
        <button type="button" onClick={onOpen} className="min-w-0 text-left">
          <p className="truncate text-[13px] font-medium text-slate-900">{opportunity.title}</p>
          <p className="truncate text-xs text-slate-500">
            {opportunity.businessName}
            {opportunity.city ? ` · ${opportunity.city}` : ''}
          </p>
        </button>
        {opportunity.access.canEdit && (
          <DropdownMenu
            trigger={(props) => (
              <button {...props} type="button" className="rounded px-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Move deal">
                ⋯
              </button>
            )}
            items={[
              { type: 'label', key: 'label', label: 'Move to' },
              ...(['qualifying', 'quote_requested', 'quote_sent', 'negotiation', 'won', 'lost'] as OpportunityStage[])
                .filter((stage) => stage !== opportunity.stage)
                .map((stage) => ({ key: stage, label: OPPORTUNITY_STAGE_LABELS[stage], danger: stage === 'lost', onSelect: () => onMove(stage) })),
            ]}
          />
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold tabular-nums text-slate-800">{formatMoney(opportunity.estimatedValue, opportunity.currency, { compact: true })}</span>
        <TierBadge tierKey={opportunity.tierKey} />
        <span className="text-slate-500">{opportunity.daysInStage}d</span>
        {opportunity.expectedCloseDate && (
          <span className={overdue ? 'text-red-600' : 'text-slate-500'}>close {formatDate(opportunity.expectedCloseDate)}</span>
        )}
        <span className="ml-auto">
          <OwnerAvatar userId={opportunity.ownerId} />
        </span>
      </div>
    </div>
  );
}

function LostReasonPanel({ opportunity, onClose, onConfirm }: { opportunity: OpportunityDTO; onClose: () => void; onConfirm: (reason: string) => void }) {
  const { meta } = useLookups();
  const [reason, setReason] = useState('');
  return (
    <SidePanel
      open
      onClose={onClose}
      title={`Mark "${opportunity.title}" as lost`}
      description="The reason is kept for reporting and for the next conversation with this pizzeria."
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={reason === ''} onClick={() => onConfirm(reason)}>
            Mark as lost
          </Button>
        </div>
      }
    >
      <Field label="Why was it lost?" required>
        <Select value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Pick a reason">
          {(meta?.settings.lostReasons ?? ['Price', 'Chose competitor', 'No budget', 'Timing / not ready', 'Other']).map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      </Field>
    </SidePanel>
  );
}

function DealDrawer({ id, onClose, onMove }: { id: string; onClose: () => void; onMove: (opportunity: OpportunityDTO, stage: OpportunityStage) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['opportunities', id],
    queryFn: ({ signal }) => api.get<OpportunityDTO>(`/api/opportunities/${id}`, { signal }),
  });
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);
  const [expectedClose, setExpectedClose] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/opportunities/${id}`, {
        version: data!.version,
        ...(quoteNumber !== null ? { quoteNumber: quoteNumber || null } : {}),
        ...(expectedClose !== null ? { expectedCloseDate: expectedClose || null } : {}),
      }),
    onSuccess: async () => {
      toast.success('Deal updated');
      await queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <SidePanel
      open
      onClose={onClose}
      title={data?.title ?? 'Deal'}
      description={data ? `${data.businessName} · ${OPPORTUNITY_STAGE_LABELS[data.stage]}` : undefined}
      width="lg"
      footer={
        data?.access.canEdit ? (
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Close</Button>
            <Button variant="primary" loading={save.isPending} disabled={quoteNumber === null && expectedClose === null} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        ) : undefined
      }
    >
      {!data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="space-y-5 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <StageBadge stage={data.stage} />
            <span className="text-slate-500">{data.daysInStage} days in stage</span>
            <Link to={`/leads/${data.businessId}?tab=deals`} className="ml-auto text-ember-700 hover:underline">
              Open the lead
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Info label="Value" value={formatMoney(data.estimatedValue, data.currency)} />
            <Info label="Probability" value={`${data.effectiveProbability}%${data.probability === null ? ' (stage default)' : ''}`} />
            <Info label="Weighted" value={formatMoney(data.weightedValue, data.currency)} />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Line items</p>
            {data.products.length === 0 ? (
              <p className="text-slate-500">No line items — value entered directly.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {data.products.map((line) => (
                  <li key={line.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="flex-1 text-slate-800">{line.description}</span>
                    <span className="text-slate-500">
                      {line.quantity} × {formatMoney(line.unitPrice, data.currency)}
                    </span>
                    <span className="w-24 text-right tabular-nums">{formatMoney(line.lineTotal, data.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {data.access.canEdit && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Quote number">
                <Input defaultValue={data.quoteNumber ?? ''} onChange={(event) => setQuoteNumber(event.target.value)} />
              </Field>
              <Field label="Expected close">
                <Input type="date" defaultValue={data.expectedCloseDate ?? ''} onChange={(event) => setExpectedClose(event.target.value)} />
              </Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Info label="Quote requested" value={data.quoteRequestedAt ? formatDate(data.quoteRequestedAt) : '—'} />
            <Info label="Quote sent" value={data.quoteSentAt ? formatDate(data.quoteSentAt) : '—'} />
            <Info label="Competitors" value={data.competitors ?? '—'} />
            <Info label="Primary contact" value={data.primaryContact?.name ?? '—'} />
            {data.lostReason && <Info label="Lost because" value={data.lostReason} />}
          </div>
          {data.access.canEdit && (
            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
              {(['qualifying', 'quote_requested', 'quote_sent', 'negotiation', 'won', 'lost'] as OpportunityStage[])
                .filter((stage) => stage !== data.stage)
                .map((stage) => (
                  <Button key={stage} size="sm" variant={stage === 'won' ? 'primary' : stage === 'lost' ? 'danger' : 'secondary'} onClick={() => onMove(data, stage)}>
                    {OPPORTUNITY_STAGE_LABELS[stage]}
                  </Button>
                ))}
            </div>
          )}
        </div>
      )}
    </SidePanel>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-slate-800">{value}</p>
    </div>
  );
}
